import { t } from "@lingui/macro"
import { GridRenderCellParams } from "@mui/x-data-grid-pro"
import { useCallback, useMemo } from "react"

import { NoWrap } from "src/components/NoWrap"
import { StatusChip } from "src/components/StatusChip"
import { Time } from "src/components/Time"
import { TruncatedText } from "src/components/TruncatedText"
import { IColumn } from "src/types/data-grid-pro"
import { IAsyncRequestItemListItem } from "src/views/async-request-items/store"

export const useGetAsyncRequestItemsColumns =
    (): IColumn<IAsyncRequestItemListItem>[] => {
        const renderStatusCell = useCallback(
            (params: GridRenderCellParams) => (
                <StatusChip
                    label={params.row.status}
                    state={getStatusChipState(params.row.status)}
                />
            ),
            [],
        )

        const renderAttemptsCell = useCallback(
            (params: GridRenderCellParams) => (
                <NoWrap>
                    {params.row.attempts}/{params.row.max_attempts}
                </NoWrap>
            ),
            [],
        )

        const renderWorkerIdCell = useCallback(
            (params: GridRenderCellParams) =>
                Boolean(params.row.worker_id) ? (
                    <NoWrap title={params.row.worker_id}>
                        <TruncatedText
                            text={params.row.worker_id ?? ""}
                            max={15}
                        />
                    </NoWrap>
                ) : (
                    "-"
                ),
            [],
        )

        const renderErrorCodeCell = useCallback(
            (params: GridRenderCellParams) =>
                Boolean(params.row.error_code) ? (
                    <NoWrap title={params.row.error_code}>
                        <TruncatedText
                            text={params.row.error_code ?? ""}
                            max={20}
                        />
                    </NoWrap>
                ) : (
                    "-"
                ),
            [],
        )

        const renderPayloadCell = useCallback(
            (params: GridRenderCellParams) =>
                Boolean(params.row.payload) ? (
                    <NoWrap title={params.row.payload}>
                        <TruncatedText
                            text={params.row.payload ?? ""}
                            max={30}
                        />
                    </NoWrap>
                ) : (
                    "-"
                ),
            [],
        )

        const renderDateCell = useCallback(
            (params: GridRenderCellParams) =>
                params.value != null ? (
                    <NoWrap>
                        <Time date={params.value} />
                    </NoWrap>
                ) : (
                    "-"
                ),
            [],
        )

        const statusOptions = useMemo(
            () => [
                {
                    value: "created",
                    label: t`async-request-items.status.created`,
                },
                {
                    value: "pending",
                    label: t`async-request-items.status.pending`,
                },
                {
                    value: "pending_send",
                    label: t`async-request-items.status.pending-send`,
                },
                { value: "sent", label: t`async-request-items.status.sent` },
                {
                    value: "processing",
                    label: t`async-request-items.status.processing`,
                },
                {
                    value: "completed",
                    label: t`async-request-items.status.completed`,
                },
                {
                    value: "failed",
                    label: t`async-request-items.status.failed`,
                },
                {
                    value: "partial",
                    label: t`async-request-items.status.partial`,
                },
            ],
            [],
        )

        return useMemo<IColumn<IAsyncRequestItemListItem>[]>(
            () => [
                {
                    field: "id",
                    headerName: t`async-request-items.id`,
                    width: 100,
                    type: "number",
                },
                {
                    field: "async_request_id",
                    headerName: t`async-request-items.async-request-id`,
                    width: 150,
                    type: "number",
                },
                {
                    field: "status",
                    headerName: t`async-request-items.status`,
                    width: 150,
                    renderCell: renderStatusCell,
                    type: "singleSelect",
                    // @ts-ignore
                    valueOptions: statusOptions,
                },
                {
                    field: "attempts",
                    headerName: t`async-request-items.attempts`,
                    width: 120,
                    renderCell: renderAttemptsCell,
                    filterable: false,
                    sortable: false,
                },
                {
                    field: "worker_id",
                    headerName: t`async-request-items.worker-id`,
                    width: 150,
                    renderCell: renderWorkerIdCell,
                    type: "string",
                },
                {
                    field: "created_at",
                    headerName: t`async-request-items.created-at`,
                    width: 180,
                    renderCell: renderDateCell,
                    type: "dateTime",
                },
                {
                    field: "processed_at",
                    headerName: t`async-request-items.processed-at`,
                    width: 180,
                    renderCell: renderDateCell,
                    type: "dateTime",
                },
                {
                    field: "error_code",
                    headerName: t`async-request-items.error-code`,
                    width: 150,
                    renderCell: renderErrorCodeCell,
                    type: "string",
                },
                {
                    field: "payload",
                    headerName: t`async-request-items.payload`,
                    width: 200,
                    renderCell: renderPayloadCell,
                    type: "json_text",
                },
            ],
            [
                renderStatusCell,
                renderAttemptsCell,
                renderWorkerIdCell,
                renderErrorCodeCell,
                renderPayloadCell,
                renderDateCell,
                statusOptions,
            ],
        )
    }

// Helper function for status chip state
function getStatusChipState(
    status: string,
): "positive" | "neutral" | "negative" | "info" | "other" | "dark" {
    switch (status) {
        case "completed":
            return "positive"
        case "failed":
            return "negative"
        case "processing":
            return "neutral"
        case "partial":
            return "neutral"
        case "pending":
        case "pending_send":
        case "sent":
            return "info"
        default:
            return "other"
    }
}
